import { cn } from '../lib/utils'

import Skeleton from './Skeleton'

/**
 * Exact-layout placeholder for one `AddressCard`: the same
 * `rounded-lg border border-border p-20` box, the same inner column, and the
 * 24px chevron block on the right.
 *
 * The four detail rows are `formatAddressLineEntries`' full shape — street,
 * city/state/postcode, country and phone under the name — and they sit in
 * their own gapless column exactly as the card renders them, so a complete
 * address swaps in without the box changing height.
 */
function AddressCardSkeleton() {
  return (
    <div
      className="flex w-full items-center rounded-lg border border-border p-20"
      data-testid="address-card-skeleton"
    >
      <span className="flex flex-1 flex-col gap-2 pb-2 pr-20">
        {/* Name row — `text-base leading-base`, so a 16px bar in a 24px box. */}
        <span className="flex h-24 items-center">
          <Skeleton h={16} className="w-[46%]" />
        </span>
        {/* Detail rows — `text-sm leading-sm`, 14px bars in 20px boxes. */}
        <span className="flex flex-col">
          <span className="flex h-20 items-center">
            <Skeleton h={14} className="w-[62%]" />
          </span>
          <span className="flex h-20 items-center">
            <Skeleton h={14} className="w-[48%]" />
          </span>
          <span className="flex h-20 items-center">
            <Skeleton h={14} className="w-[30%]" />
          </span>
          <span className="flex h-20 items-center">
            <Skeleton h={14} className="w-[40%]" />
          </span>
        </span>
      </span>
      <Skeleton w={24} h={24} r={4} className="-mr-2 shrink-0" />
    </div>
  )
}

/**
 * The address list's loading state (RP-5936): one address card placeholder
 * plus the "Add address" card, which the loaded list always renders until the
 * account hits `MAX_ADDRESSES`.
 *
 * The address count cannot be known before the request lands, so an account
 * with several addresses still grows the list on arrival — unavoidable for a
 * variable-length list. One card is the common case and keeps the panel from
 * opening on an empty column.
 */
export default function AddressListSkeleton({
  isReserved,
}: {
  /** Reserve the layout without painting it (the anti-flash window). */
  isReserved?: boolean
}) {
  return (
    <div
      className={cn('flex flex-col gap-12', isReserved && 'invisible')}
      aria-busy="true"
      data-testid="address-list-skeleton"
    >
      <AddressCardSkeleton />
      {/* Mirrors the "Add address" button: same box, a label bar and the
          24px plus icon. */}
      <div className="flex w-full items-center rounded-lg border border-border p-20">
        <span className="flex-1">
          <Skeleton h={16} className="w-[38%]" />
        </span>
        <Skeleton w={24} h={24} r={4} className="-mr-2 shrink-0" />
      </div>
    </div>
  )
}
